import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * IOSInstallBanner — Banner discreto visualizzato solo agli utenti iPhone/iPad
 * (Safari) che NON hanno ancora installato la PWA. Suggerisce l'aggiunta alla
 * schermata Home come un'app nativa.
 *
 * Dismissibile (ricordato per 30 giorni in localStorage).
 */

const DISMISS_KEY = 'rip_pwa_ios_dismiss_until';

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPhone / iPad / iPod. iPadOS 13+ si spaccia per Mac → controllo touch.
  const ipados = /Macintosh/.test(ua) && 'ontouchend' in document;
  return /iPad|iPhone|iPod/.test(ua) || ipados;
};

const isInStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true);

const IOSInstallBanner = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isIOS()) return;
    if (isInStandalone()) return;
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (until && Date.now() < until) return;
    // Mostra dopo 3 secondi per non distrarre il primo paint
    const t = setTimeout(() => setShow(true), 3000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    // Ricorda per 30 giorni
    const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(Date.now() + thirtyDaysMs));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-3 z-[60] sm:left-auto sm:right-4 sm:max-w-sm rounded-2xl shadow-2xl border border-white/10 backdrop-blur-md"
      style={{
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
        background: 'linear-gradient(135deg, rgba(60, 56, 54, 0.97) 0%, rgba(45, 41, 39, 0.97) 100%)',
        color: '#fff',
      }}
      data-testid="ios-install-banner"
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-12 h-12 rounded-xl bg-[#C5A059] flex items-center justify-center shadow-md">
            <img loading="lazy" decoding="async"
              src="/icons/icon-192x192.png"
              alt="Resta in Pace"
              className="w-9 h-9 rounded-lg"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-base leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Installa Resta in Pace
            </p>
            <p className="text-xs text-white/70 mt-0.5 leading-relaxed">
              Aggiungi l&apos;app alla schermata Home in pochi secondi, senza App Store.
            </p>
          </div>
          <button
            onClick={dismiss}
            className="shrink-0 w-7 h-7 rounded-full text-white/70 hover:bg-white/10 flex items-center justify-center text-sm"
            aria-label="Chiudi"
            data-testid="ios-install-banner-close"
          >
            ✕
          </button>
        </div>

        <ol className="mt-3 space-y-1.5 text-xs text-white/85">
          <li className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center text-[10px] font-bold">1</span>
            Tocca <span className="inline-block px-1.5 py-0.5 bg-white/15 rounded text-[11px]">⎙ Condividi</span> in Safari
          </li>
          <li className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center text-[10px] font-bold">2</span>
            Scorri e scegli <span className="font-semibold">Aggiungi a Home</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center text-[10px] font-bold">3</span>
            Tocca <span className="font-semibold">Aggiungi</span>. Fatto!
          </li>
        </ol>

        <div className="mt-3 flex items-center gap-2">
          <Link
            to="/app"
            onClick={dismiss}
            className="flex-1 text-center px-3 py-2 rounded-lg bg-[#C5A059] text-[#2d2924] font-semibold text-sm hover:bg-[#d4b070] transition"
            data-testid="ios-install-banner-guide"
          >
            Guida completa
          </Link>
          <button
            onClick={dismiss}
            className="px-3 py-2 text-xs text-white/60 hover:text-white"
            data-testid="ios-install-banner-later"
          >
            Più tardi
          </button>
        </div>
      </div>
    </div>
  );
};

export default IOSInstallBanner;
